import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@clerk/react";

export interface FavouriteTender {
  tenderId: number;
  savedAt: string;
  title: string;
  district: string | null;
  status: string;
  deadlineDate: string | null;
}

async function authFetch(url: string, getToken: () => Promise<string | null>, options?: RequestInit) {
  const token = await getToken();
  return fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
}

export function useCheckFavourite(tenderId: number, enabled: boolean) {
  const { getToken } = useAuth();
  return useQuery({
    queryKey: ["favourite-check", tenderId],
    queryFn: async () => {
      const res = await authFetch(`/api/favourites/check/${tenderId}`, getToken);
      if (!res.ok) return { isFavourited: false };
      return res.json() as Promise<{ isFavourited: boolean }>;
    },
    enabled: enabled && tenderId > 0,
    staleTime: 30_000,
  });
}

export function useGetFavourites(enabled: boolean) {
  const { getToken } = useAuth();
  return useQuery({
    queryKey: ["favourites"],
    queryFn: async () => {
      const res = await authFetch("/api/favourites", getToken);
      if (!res.ok) throw new Error("Failed to fetch favourites");
      return res.json() as Promise<{ favourites: FavouriteTender[] }>;
    },
    enabled,
  });
}

export function useToggleFavourite() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ tenderId, isFavourited }: { tenderId: number; isFavourited: boolean }) => {
      const method = isFavourited ? "DELETE" : "POST";
      const res = await authFetch(`/api/favourites/${tenderId}`, getToken, { method });
      if (!res.ok) throw new Error("Failed to toggle favourite");
      return res.json();
    },
    onSuccess: (_data, { tenderId }) => {
      queryClient.invalidateQueries({ queryKey: ["favourite-check", tenderId] });
      queryClient.invalidateQueries({ queryKey: ["favourites"] });
    },
  });
}
